/**
 * TransactionHistory
 *
 * Read-only table of past credit transactions.
 */
import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  Chip,
  Box,
  Skeleton,
} from '@mui/material';

const TYPE_CONFIG = {
  purchase:   { label: 'Purchase',   color: 'success' },
  usage:      { label: 'Usage',      color: 'default' },
  refund:     { label: 'Refund',     color: 'info'    },
  bonus:      { label: 'Bonus',      color: 'secondary' },
  adjustment: { label: 'Adjustment', color: 'warning' },
};

const friendlyDescription = (tx) => {
  if (tx.description) return tx.description;
  const ref = tx.reference || '';
  if (ref === 'SIGNUP_BONUS' || ref === 'SIGNUP_BONUS_BACKFILL') return 'Welcome bonus';
  if (ref.startsWith('ADMIN-GRANT-')) return 'Admin credit grant';
  return ref || '—';
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const TransactionHistory = ({ transactions, loading }) => {
  if (loading) {
    return (
      <Box>
        {[0, 1, 2].map(i => <Skeleton key={i} height={40} sx={{ my: 0.5 }} />)}
      </Box>
    );
  }

  if (!transactions?.length) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
        No transactions yet.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map(tx => {
            const cfg = TYPE_CONFIG[tx.type] || { label: tx.type, color: 'default' };
            const sign = tx.amount >= 0 ? '+' : '';
            return (
              <TableRow key={tx.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                  {fmtDate(tx.created)}
                </TableCell>
                <TableCell>
                  <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontSize: 11 }} />
                </TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {friendlyDescription(tx)}
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={tx.amount >= 0 ? 'success.main' : 'text.secondary'}
                  >
                    {sign}{tx.amount}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {tx.balance_after}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TransactionHistory;
